import { useParams, Link, Navigate } from "react-router-dom";
import SiteFooter from "@/components/SiteFooter";
import { getApp, APPS, type AppId, WAVLYNQ_LOGO } from "@/lib/apps";
import { Users, Shield, Sparkles, CalendarDays, MessageCircle, Heart } from "lucide-react";
import { trackEvent } from "@/lib/analytics/track";

/** Per-app "what to do next" steps. Each app highlights what matters most to its community. */
const APP_STEPS: Record<AppId, { icon: React.ElementType; title: string; description: string }[]> = {
  fryqnc: [
    { icon: Users, title: "Build your profile", description: "Share your identity on your terms — pronouns, gender identity, orientation, and the prompts that matter to you." },
    { icon: Heart, title: "Discover your people", description: "Priority-matching finds connections who actually get it — dating, friendship, or both." },
    { icon: Sparkles, title: "Meet RAELA", description: "Your crisis-aware, community-tuned AI companion — always on, always here." },
    { icon: CalendarDays, title: "Join community events", description: "In-person and virtual gatherings built for LGBTQIA+ members, hosted by verified organizers." },
    { icon: Shield, title: "Safety first", description: "Emergency Button, ID verification, and crisis resources are built in from day one." },
    { icon: MessageCircle, title: "Find a mentor", description: "Verified mentors who've been where you are — career, identity, transitions, and more." },
  ],
  pulsynq: [
    { icon: Users, title: "Build your profile", description: "Show up authentically — your story, your photos, your voice intro." },
    { icon: Heart, title: "Find your people", description: "Culturally grounded matches rooted in the Black experience — dating, friendship, or mentorship." },
    { icon: Sparkles, title: "Meet RAELA", description: "An AI companion tuned to the pulse of the Black community — crisis-aware and always available." },
    { icon: CalendarDays, title: "Attend community events", description: "Gatherings rooted in our culture — curated by the community, for the community." },
    { icon: Shield, title: "Safety built in", description: "Emergency Button, crisis resources, and ID verification — because your safety isn't optional." },
    { icon: MessageCircle, title: "Connect with mentors", description: "Guidance from people who understand your journey — career, wellness, and creative practice." },
  ],
  mindlynq: [
    { icon: Users, title: "Build your profile", description: "Share how you experience the world — sensory preferences, neurodivergence, communication style." },
    { icon: Heart, title: "Discover matches", description: "Sensory-aware discovery that respects how you connect — at your own pace." },
    { icon: Sparkles, title: "Meet RAELA + RESONATE", description: "RAELA is your AI companion. RESONATE is MindLynq-exclusive — sensory and emotional attunement tools." },
    { icon: CalendarDays, title: "Join sensory-friendly events", description: "Events designed with accessibility in mind — clear expectations, low overwhelm." },
    { icon: Shield, title: "Safety & support", description: "Emergency Button, crisis resources, and a calm, predictable interface." },
    { icon: MessageCircle, title: "Find a mentor", description: "Neurodivergent mentors who actually get it — ADHD, autism, sensory processing, and beyond." },
  ],
  ablelynq: [
    { icon: Users, title: "Build your profile", description: "Share your story — accessibility needs, communication preferences, and what connection means to you." },
    { icon: Heart, title: "Find your people", description: "Accessibility-first matching that meets you where you are." },
    { icon: Sparkles, title: "Meet RAELA", description: "Your AI companion — designed for accessibility, tuned to the disability community." },
    { icon: CalendarDays, title: "Accessible events", description: "Gatherings designed with accessibility at the center — not as an afterthought." },
    { icon: Shield, title: "Safety & advocacy", description: "Emergency Button, crisis resources, and a platform that fights for your visibility." },
    { icon: MessageCircle, title: "Peer mentorship", description: "Mentors who understand disability from the inside — lived experience, real guidance." },
  ],
  deafwav: [
    { icon: Users, title: "Build your profile", description: "Visual-first profiles — share your communication style, ASL fluency, and how you connect." },
    { icon: Heart, title: "Discover connections", description: "Matching that understands Deaf culture — visual communication, shared identity, mutual respect." },
    { icon: Sparkles, title: "Meet RAELA", description: "Your AI companion — designed for visual-first communication and Deaf cultural awareness." },
    { icon: CalendarDays, title: "Community gatherings", description: "Events that center Deaf culture — ASL-friendly, visually accessible, community-driven." },
    { icon: Shield, title: "Safety & resources", description: "Emergency Button with video relay support, crisis resources, and ID verification." },
    { icon: MessageCircle, title: "Deaf mentors", description: "Mentors from the Deaf and HoH community — career, identity, and cultural guidance." },
  ],
  fullynq: [
    { icon: Users, title: "Build your profile", description: "Show up fully — no filters, no shame. Your photos, your story, your way." },
    { icon: Heart, title: "Find your people", description: "Body-affirming matches — dating, friendship, and community without compromise." },
    { icon: Sparkles, title: "Meet RAELA", description: "Your AI companion — body-positive, crisis-aware, and judgment-free." },
    { icon: CalendarDays, title: "Body-positive events", description: "Gatherings that celebrate every body — fitness, fashion, wellness, and connection." },
    { icon: Shield, title: "Safety & support", description: "Emergency Button, crisis resources, and eating disorder awareness built in." },
    { icon: MessageCircle, title: "Supportive mentors", description: "Mentors who celebrate you — body image, wellness, confidence, and living fully." },
  ],
  jadesynq: [
    { icon: Users, title: "Build your profile", description: "Share your heritage, languages, and cultural roots — Pan-Asian identity celebrated." },
    { icon: Heart, title: "Find your community", description: "Culturally grounded matches across the Asian diaspora — dating, friendship, mentorship." },
    { icon: Sparkles, title: "Meet RAELA", description: "Your AI companion — culturally aware, multilingual-friendly, always available." },
    { icon: CalendarDays, title: "Cultural events", description: "Celebrations, workshops, and gatherings rooted in Pan-Asian traditions and modern identity." },
    { icon: Shield, title: "Safety first", description: "Emergency Button, crisis resources, and a space free from fetishization and stereotypes." },
    { icon: MessageCircle, title: "Intergenerational mentors", description: "Mentors who bridge generations — career, culture, identity, and diaspora navigation." },
  ],
  openlynq: [
    { icon: Users, title: "Build your profile", description: "Share your relationship style — poly, ENM, open. Clear communication starts here." },
    { icon: Heart, title: "Discover connections", description: "Consent-first matching for open, poly, and ethically non-monogamous relationships." },
    { icon: Sparkles, title: "Meet RAELA", description: "Your AI companion — consent-aware, judgment-free, and tuned to ENM community needs." },
    { icon: CalendarDays, title: "Community events", description: "Meetups and workshops for the ENM community — education, connection, and celebration." },
    { icon: Shield, title: "Safety & consent", description: "Emergency Button, crisis resources, and consent-first design throughout." },
    { icon: MessageCircle, title: "Relationship mentors", description: "Mentors experienced in open relationships — communication, boundaries, and growth." },
  ],
  spiritsynq: [
    { icon: Users, title: "Build your profile", description: "Share your faith, spiritual practice, and how you connect — reverent and authentic." },
    { icon: Heart, title: "Find your community", description: "Matches rooted in shared faith and spiritual practice — dating, friendship, or guidance." },
    { icon: Sparkles, title: "Meet RAELA", description: "Your AI companion — spiritually aware, crisis-trained, and respectful of all practices." },
    { icon: CalendarDays, title: "Faith & wellness events", description: "Gatherings centered on spirituality — meditation, worship, community service, and connection." },
    { icon: Shield, title: "Safety & peace", description: "Emergency Button, crisis resources, and a safe space for spiritual exploration." },
    { icon: MessageCircle, title: "Spiritual mentors", description: "Mentor-led guidance rooted in faith — recovery, transitions, purpose, and practice." },
  ],
};

const Welcome = () => {
  const { appId } = useParams<{ appId: string }>();

  const validIds = APPS.map((a) => a.id) as string[];
  if (!appId || !validIds.includes(appId)) {
    return <Navigate to="/" replace />;
  }

  const app = getApp(appId as AppId);
  const steps = APP_STEPS[appId as AppId];

  return (
    <div className={`min-h-screen flex flex-col bg-background text-foreground ${app.themeClass}`}>
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to={app.href} className="flex items-center" aria-label={`${app.name} home`}>
            <img src={app.logo} alt={app.name} className="h-7 w-auto" />
          </Link>
          <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 text-sm">
            <img src={WAVLYNQ_LOGO} alt="" className="h-4 w-auto" aria-hidden />
            <span>Network</span>
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className={`absolute inset-x-0 top-0 h-1 ${app.gradientClass}`} aria-hidden />
          <div className={`absolute -top-40 -right-40 h-[28rem] w-[28rem] rounded-full opacity-15 blur-3xl ${app.gradientClass}`} aria-hidden />

          <div className="relative mx-auto max-w-4xl px-6 pt-20 pb-12 md:pt-28 md:pb-16 text-center">
            <img src={app.icon} alt="" className="mx-auto h-16 w-16" />
            <h1 className="mt-6 text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
              Welcome to {app.name}
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground leading-relaxed">
              Here's what you can do once you're in. Set up takes just a few minutes.
            </p>
          </div>
        </section>

        {/* Steps */}
        <section className="border-t border-border bg-secondary/30">
          <div className="mx-auto max-w-4xl px-6 py-16 md:py-20">
            <div className="grid gap-6 md:grid-cols-2">
              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div
                    key={i}
                    className="flex gap-4 rounded-2xl border border-border bg-background p-5 transition-colors hover:border-foreground/20"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary">
                      <Icon className="h-5 w-5 text-foreground" />
                    </div>
                    <div>
                      <p className="font-semibold">{s.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                        {s.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="mt-12 flex flex-col items-center gap-4">
              <Link
                to={`/auth?next=/onboarding`}
                onClick={() => trackEvent("welcome_create_account", { app: appId as AppId })}
                className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Create your account
              </Link>
              <p className="text-xs text-muted-foreground">
                Already a member?{" "}
                <Link to={`/auth?next=/account`} className="text-foreground underline underline-offset-2">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Welcome;
